import { Injectable } from '@angular/core'
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { environment } from '../../../../environments/environment'
import { PaginationModel } from '../../../models/pagination.model'
import { IChangeLogModel, IRequestLogModel } from '../../../models/history-records-change.model'
import { HelperService } from '../../helper.service'
import { IBulkUploadProcessModel } from '../../../models/admin/configuration/bulk-upload-process.model'
import { IConfigurationEditorErrorsModel } from '../../../models/admin/configuration/configuration-editor-errors.model'
import IHistoryTrackingService from '../../../pages/spas/common/history-tracking/i-history-tracking.service'
import IBulkUploadProcessHistoryService from '../../../pages/spas/admin/common/bulk-upload-process/bulk-upload-process-history/i-bulk-upload-process-history.service'

@Injectable({
  providedIn: 'root',
})
export class SoraEditorService
  implements IHistoryTrackingService, IBulkUploadProcessHistoryService
{
  private apiURL: string = environment.apiUrl + '/admin/config/sora-register'

  constructor(private http: HttpClient) {}

  private getHeaders(): HttpHeaders {
    return new HttpHeaders().set('Content-Type', 'application/json')
  }

  private handleEventMessage(event: HttpEvent<any>) {
    switch (event.type) {
      case HttpEventType.DownloadProgress:
      case HttpEventType.UploadProgress:
        return this.fileUploadProgress(event)
      case HttpEventType.Response:
        return this.apiResponse(event)
      default:
        return `Unknown event type`
    }
  }

  private fileUploadProgress(event: any): { status: string; message: number } {
    const percent: number = Math.round((100 * event.loaded) / event.total)
    return { status: 'Progress', message: percent }
  }

  private apiResponse(event: any) {
    return event.body
  }

  public downloadSoraRegisterFile(): Observable<any> {
    const url: string = this.apiURL + '/download'
    return this.http
      .get(url, { reportProgress: true, observe: 'events' })
      .pipe(map((event) => this.handleEventMessage(event)))
  }

  public uploadSoraRegisterFile(file: File, acr: string): Observable<any> {
    const formData: FormData = new FormData()
    formData.append('file', file)
    const url: string = this.apiURL + '/upload?acr=' + encodeURIComponent(acr)
    return this.http
      .post(url, formData, { reportProgress: true, observe: 'events' })
      .pipe(map((event) => this.handleEventMessage(event)))
  }

  public getBulkUploadProcess(
    processId?: string,
  ): Observable<{ process: IBulkUploadProcessModel | null }> {
    const queryString: string =
      processId && processId !== 'fake-process-id' ? '?id=' + encodeURIComponent(processId) : ''
    const url: string = this.apiURL + '/upload/process/active' + queryString
    return this.http
      .get<{ process: IBulkUploadProcessModel | null }>(url, { headers: this.getHeaders() })
      .pipe(
        map((resource: { process: IBulkUploadProcessModel | null }) => {
          if (resource.process && resource.process.type === 'SORA') {
            return resource
          }
          return { process: null }
        }),
      )
  }

  public getHistoryEvents(
    pagination: PaginationModel,
    params?: any,
  ): Observable<{ count: number; items: IRequestLogModel[] }> {
    const query: string = HelperService.complexObjectToQuery({ pagination: pagination })
    const url: string = this.apiURL + '/history/events?' + query
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: { count: number; items: IRequestLogModel[] }) => {
        return resource
      }),
    )
  }

  public getHistoryEventItems(
    processId: string,
    pagination: PaginationModel,
  ): Observable<{ count: number; items: IChangeLogModel[] }> {
    const query: string = HelperService.complexObjectToQuery({ pagination: pagination })
    const url: string =
      this.apiURL + '/history/events/' + encodeURIComponent(processId) + '/items?' + query
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: { count: number; items: IChangeLogModel[] }) => {
        return resource
      }),
    )
  }

  public getBulkUploadProcessHistory(
    pagination: PaginationModel,
  ): Observable<{ count: number; items: any[] }> {
    const query: string = HelperService.complexObjectToQuery({ pagination: pagination })
    const url: string = this.apiURL + '/history/upload/process?' + query
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: { count: number; items: any[] }) => {
        return resource
      }),
    )
  }

  public downloadBulkUploadValidationErrorsFile(processId: string): Observable<any> {
    const url: string =
      this.apiURL +
      '/history/upload/process/validation-errors/download/' +
      encodeURIComponent(processId)
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource) => {
        return resource['base64']
      }),
    )
  }

  public getBulkUploadValidationErrors(
    processId: string,
    pagination: PaginationModel,
  ): Observable<{ count: number; items: any[] }> {
    const query: string = HelperService.complexObjectToQuery({ pagination: pagination })
    const url: string =
      this.apiURL +
      '/history/upload/process/validation-errors/' +
      encodeURIComponent(processId) +
      '?' +
      query
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: { count: number; items: IConfigurationEditorErrorsModel[] }) => {
        return resource
      }),
    )
  }

  public downloadSoraFormArchive(type: 'doc' | 'pdf'): Observable<any> {
    const url: string = this.apiURL + '/download/' + type + '/forms/archive'
    return this.http
      .get(url, { responseType: 'blob', reportProgress: true, observe: 'events' })
      .pipe(
        map((event: HttpEvent<Blob>): {status: string, message?: number, blob?: any, fileName?: string} => {
          switch (event.type) {
            case HttpEventType.DownloadProgress:
              return {
                status: 'Progress',
                message: Math.round((100 * event.loaded) / (event.total || event.loaded)),
              }
            case HttpEventType.Response:
              const disposition: string | null = event.headers.get('Content-Disposition');
              const fileName: string = disposition
                ? disposition.split('filename=')[1]?.replace(/['"]/g, '').trim()
                : `sora_forms_${type}.zip`

              return { status: 'Completed', blob: event.body, fileName: fileName }
            default:
              return null
          }
        }),
      )
  }

  public downloadSoraFormFile(type: 'doc' | 'pdf', mainObject: string): Observable<any> {
    const url: string =
      this.apiURL + '/download/' + type + '/form?mo=' + encodeURIComponent(mainObject)
    return this.http
      .get(url, { reportProgress: true, observe: 'events' })
      .pipe(map((event) => this.handleEventMessage(event)))
  }
}
